import { Box, Typography } from '@mui/material'
import { ReactNode } from 'react'
import { COLORS } from '../theme'

interface SelectableCardProps {
  label: string
  description?: string
  value: string
  icon?: ReactNode
  selected: boolean
  onClick: (value: string) => void
  disabled?: boolean
  layout?: 'horizontal' | 'vertical'
}

export default function SelectableCard({
  label,
  description,
  value,
  icon,
  selected,
  onClick,
  disabled = false,
  layout = 'horizontal',
}: SelectableCardProps) {
  return (
    <Box
      role="radio"
      aria-checked={selected}
      aria-disabled={disabled || undefined}
      aria-label={description ? `${label}: ${description}` : label}
      tabIndex={disabled ? -1 : 0}
      onClick={disabled ? undefined : () => onClick(value)}
      onKeyDown={
        disabled
          ? undefined
          : (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onClick(value)
              }
            }
      }
      sx={{
        display: 'flex',
        flexDirection: layout === 'vertical' ? 'column' : 'row',
        alignItems: 'center',
        gap: layout === 'vertical' ? 1.5 : 2,
        p: layout === 'vertical' ? 3 : 2,
        minHeight: layout === 'vertical' ? 'auto' : 64,
        border: selected ? `2px solid ${COLORS.primary}` : `2px solid ${COLORS.border}`,
        borderRadius: '12px',
        backgroundColor: selected ? COLORS.primaryBgSubtle : 'white',
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1,
        transition: disabled ? undefined : 'all 0.15s ease',
        textAlign: layout === 'vertical' ? 'center' : 'left',
        ...(!disabled && {
          '&:hover': {
            borderColor: COLORS.primary,
            backgroundColor: selected ? COLORS.primaryBgSubtle : COLORS.primaryBgFaint,
          },
          '&:focus-visible': {
            outline: `2px solid ${COLORS.primary}`,
            outlineOffset: 2,
          },
        }),
      }}
    >
      {icon && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: selected ? COLORS.primary : COLORS.secondary,
            fontSize: 28,
          }}
        >
          {icon}
        </Box>
      )}
      <Box>
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 600,
            color: disabled ? COLORS.lightGray : COLORS.primary,
          }}
        >
          {label}
        </Typography>
        {description && (
          <Typography
            variant="body2"
            sx={{
              color: disabled ? COLORS.disabledIcon : COLORS.secondary,
              mt: 0.25,
            }}
          >
            {description}
          </Typography>
        )}
      </Box>
    </Box>
  )
}
